import { Link, Outlet, useLocation } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { ErrorBoundarySuspense } from '@/components/error-boundary-suspense';
import { Tabs, TabsList } from '@/components/ui/tabs';
import { PurchaseOrderLayoutHeader } from '@/features/purchase-order/layout/purchase-order-layout-header';
import { PurchaseOrderLayoutHeaderSkeleton } from '@/features/purchase-order/layout/purchase-order-layout-skeleton';
import { GET_PURCHASE_ORDER_KEY } from '@/keys';
import { cn } from '@/lib/utils';

const detailTabs = [
  { path: '', labelKey: 'tabGeneral' },
  { path: '/products', labelKey: 'tabReferences' },
  { path: '/activity', labelKey: 'tabActivity' },
  { path: '/documents', labelKey: 'tabDocuments' },
] as const;

type PurchaseOrderLayoutProps = {
  purchaseOrderId: string;
};

export function PurchaseOrderLayout({
  purchaseOrderId,
}: PurchaseOrderLayoutProps) {
  const { t } = useTranslation('purchaseOrder');
  const location = useLocation();
  const basePath = `/purchase-orders/${purchaseOrderId}`;

  return (
    <div className='flex min-h-0 flex-1 flex-col gap-4'>
      <div className='flex flex-col gap-3'>
        <ErrorBoundarySuspense
          errorFallbackWrapper={
            <div className='flex h-full w-full items-center justify-center p-4' />
          }
          loadingFallback={<PurchaseOrderLayoutHeaderSkeleton />}
          message={t('Unable to load purchase order')}
          queryKey={[...GET_PURCHASE_ORDER_KEY, purchaseOrderId]}
        >
          <PurchaseOrderLayoutHeader purchaseOrderId={purchaseOrderId} />
        </ErrorBoundarySuspense>
      </div>
      <Tabs>
        <TabsList className='w-fit border-b' variant='line'>
          {detailTabs.map((tab) => {
            const href = `${basePath}${tab.path}`;
            const isActive = location.pathname === href;
            return (
              <Link
                className={cn(
                  'relative inline-flex h-[calc(100%-1px)] flex-1 items-center justify-center gap-1.5 whitespace-nowrap rounded-md border border-transparent px-2 py-1 font-medium text-sm transition-all hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                  isActive
                    ? 'text-foreground after:absolute after:inset-x-0 after:bottom-[-5px] after:h-0.5 after:bg-foreground'
                    : 'text-foreground/60',
                )}
                key={tab.path || 'general'}
                params={{ id: purchaseOrderId }}
                replace={true}
                to={`/purchase-orders/$id${tab.path}`}
              >
                {t(tab.labelKey)}
              </Link>
            );
          })}
        </TabsList>
      </Tabs>
      <div className='min-h-0 flex-1'>
        <Outlet />
      </div>
    </div>
  );
}
